import AbstractComponent from "./abstract-component";

export default class Testimony extends AbstractComponent {
	constructor(el) {
		super(el);
		this.DOM = {};
		this.currentIndex = 0;
		this.testimonies = [];
		this.initDomElements();
		this.initTestimonies();
		this.bindArrows();
	}

	initDomElements() {
		this.DOM = {};
		this.DOM.imagesContainer = this.$el.querySelector('.c-testimony__track__images');
		this.DOM.quotesContainer = this.$el.querySelector('.c-testimony__track__quotes');
		this.DOM.imageItems = this.$el.querySelectorAll('.c-testimony__track__images__item');
		this.DOM.quoteItems = this.$el.querySelectorAll('.c-testimony__track__quotes__item');
		this.DOM.arrowNext = this.$el.querySelector('.c-testimony__arrow.right');
		this.DOM.arrowPrev = this.$el.querySelector('.c-testimony__arrow.left');
	}

	initTestimonies() {
		if (!this.DOM.imageItems || !this.DOM.quoteItems) return;
		
		this.testimonies = Array.from(this.DOM.quoteItems).map((item, index) => {
			const imageItem = this.DOM.imageItems[index];
			return {
				image: imageItem,
				quote: item,
				quoteText: item.querySelector('.c-testimony__track__quotes__item__quote'),
				info: item.querySelector('.c-testimony__track__quotes__item__info')
			};
		});

		// Initialiser les éléments avec GSAP
		this.testimonies.forEach((testimony, index) => {
			if (index === 0) {
				// Premier élément : visible
				if (testimony.quoteText) {
					this.app.gsap.set(testimony.quoteText, { x: 0, autoAlpha: 1 });
				}
				if (testimony.info) {
					this.app.gsap.set(testimony.info, { x: 0, autoAlpha: 1 });
				}
			} else {
				// Autres éléments : cachés
				if (testimony.quoteText) {
					this.app.gsap.set(testimony.quoteText, { x: 0, autoAlpha: 0 });
				}
				if (testimony.info) {
					this.app.gsap.set(testimony.info, { x: 0, autoAlpha: 0 });
				}
			}
		});

		// Initialiser le premier témoignage
		if (this.testimonies.length > 0) {
			this.currentIndex = 0;
			this.updateArrowsState();
		}
	}

	updateArrowsState() {
		if (!this.DOM.arrowPrev || !this.DOM.arrowNext) return;
		const isBeginning = this.currentIndex === 0;
		const isEnd = this.currentIndex === this.testimonies.length - 1;
		this.DOM.arrowPrev.disabled = isBeginning;
		this.DOM.arrowNext.disabled = isEnd;
	}

	animateOut(element, direction = 'right') {
		return new Promise((resolve) => {
			// Si on va à droite, l'élément sort à gauche (négatif)
			// Si on va à gauche, l'élément sort à droite (positif)
			const xValue = direction === 'right' ? -30 : 30;
			this.app.gsap.to(element, {
				x: xValue,
				autoAlpha: 0,
				duration: 0.4,
				ease: "power2.in",
				onComplete: resolve
			});
		});
	}

	animateIn(element, direction = 'right') {
		// Si on va à droite, l'élément entre depuis la droite (positif)
		// Si on va à gauche, l'élément entre depuis la gauche (négatif)
		const xValue = direction === 'right' ? 30 : -30;
		this.app.gsap.set(element, { x: xValue, autoAlpha: 0 });
		this.app.gsap.to(element, {
			x: 0,
			autoAlpha: 1,
			duration: 0.5,
			ease: "power2.out"
		});
	}

	goToSlide(index) {
		if (index < 0 || index >= this.testimonies.length || index === this.currentIndex) return;

		const currentTestimony = this.testimonies[this.currentIndex];
		const nextTestimony = this.testimonies[index];
		const direction = index > this.currentIndex ? 'right' : 'left';

		// Animation de sortie
		const exitPromises = [];
		
		// Animer l'image (fade simple)
		if (currentTestimony.image) {
			this.app.gsap.to(currentTestimony.image, {
				autoAlpha: 0,
				duration: 0.4,
				ease: "power2.in"
			});
		}

		// Animer le texte du témoignage
		if (currentTestimony.quoteText) {
			exitPromises.push(this.animateOut(currentTestimony.quoteText, direction));
		}

		// Animer les infos (nom + lieu) - sortie dans la direction opposée
		if (currentTestimony.info) {
			const xValue = direction === 'right' ? -30 : 30;
			this.app.gsap.to(currentTestimony.info, {
				x: xValue,
				autoAlpha: 0,
				duration: 0.4,
				ease: "power2.in"
			});
		}

		// Attendre la fin des animations de sortie
		Promise.all(exitPromises).then(() => {
			// Changer les classes actives
			currentTestimony.image?.classList.remove('is-active');
			currentTestimony.quote?.classList.remove('is-active');
			nextTestimony.image?.classList.add('is-active');
			nextTestimony.quote?.classList.add('is-active');

			// Animation d'entrée
			// Animer l'image (fade simple)
			if (nextTestimony.image) {
				this.app.gsap.set(nextTestimony.image, { autoAlpha: 0 });
				this.app.gsap.to(nextTestimony.image, {
					autoAlpha: 1,
					duration: 0.5,
					ease: "power2.out"
				});
			}

			// Animer le texte du témoignage
			if (nextTestimony.quoteText) {
				this.animateIn(nextTestimony.quoteText, direction);
			}

			// Animer les infos (nom + lieu) - entrée depuis la direction
			if (nextTestimony.info) {
				const xValue = direction === 'right' ? 30 : -30;
				this.app.gsap.set(nextTestimony.info, { x: xValue, autoAlpha: 0 });
				this.app.gsap.to(nextTestimony.info, {
					x: 0,
					autoAlpha: 1,
					duration: 0.5,
					ease: "power2.out",
					delay: 0.1
				});
			}
		});

		this.currentIndex = index;
		this.updateArrowsState();
	}

	bindArrows() {
		if (!this.DOM.arrowNext || !this.DOM.arrowPrev) return;
		
		this.DOM.arrowNext.addEventListener('click', () => {
			if (this.currentIndex < this.testimonies.length - 1) {
				this.goToSlide(this.currentIndex + 1);
			}
		});

		this.DOM.arrowPrev.addEventListener('click', () => {
			if (this.currentIndex > 0) {
				this.goToSlide(this.currentIndex - 1);
			}
		});
	}

	destroy() {
		// Nettoyer les event listeners
		if (this.DOM.arrowNext) {
			this.DOM.arrowNext.replaceWith(this.DOM.arrowNext.cloneNode(true));
		}
		if (this.DOM.arrowPrev) {
			this.DOM.arrowPrev.replaceWith(this.DOM.arrowPrev.cloneNode(true));
		}
	}
}
